/**
 * 기간 기반 쿨타임 (Phaser/도메인 비의존, 순수 로직).
 *
 * "2시간마다 광고 보상", "5분마다 자동 충전" 등 **마지막 사용 시각으로부터 일정 기간 재사용 불가**한
 * 모든 카운터에 사용. 남은 시간 판정은 이 패키지가, **시작 시각·지속시간의 영속 저장은 프로젝트가
 * `CooldownStore` Port로 위임받는다**(DIP). 시계(`Clock`)를 주입받아 테스트 결정론을 확보한다
 * — `@yjsgames/daily-reset`의 `ResetStore`/`Clock` 주입과 동형.
 *
 * 레퍼런스: idleblade `unity-timer`/`CooltimeManager`(StartCooltime/IsOnCooltime/ReduceCooltimeSeconds)
 * 를 TS로 이식. idleblade와 동일하게 시작 시각 + 지속시간을 둘 다 저장해 키마다 다른 쿨타임 길이를 허용한다.
 */

/** 현재 시각(epoch ms)을 반환하는 시계 소스. 주입으로 테스트 결정론 확보 (기본 `Date.now`). */
export type Clock = () => number;

/** 쿨타임 1건의 영속 상태 — 시작 시각 + 지속시간. 남은 시간 = (started+duration) - now. */
export interface CooldownEntry {
  /** 쿨타임 시작 시각(epoch ms). */
  readonly startedAtMs: number;
  /** 쿨타임 지속시간(ms). 키마다 다를 수 있어 entry에 함께 저장한다. */
  readonly durationMs: number;
}

/**
 * 쿨타임 entry 영속 Port — DIP. 패키지는 "남은 시간이 얼마인가"만 판정하고,
 * 실제 entry 읽기/쓰기/삭제는 프로젝트가 SaveData 등 저장소에 구현한다.
 * (idleblade `CooltimeSaveEntry`가 담당하던 역할의 TS 대응.)
 */
export interface CooldownStore {
  /** key의 현재 쿨타임 entry. 진행 이력이 없으면 `undefined`. */
  getEntry(key: string): CooldownEntry | undefined;
  /** key의 쿨타임 entry를 기록(start/reduce 시). */
  setEntry(key: string, entry: CooldownEntry): void;
  /** key의 쿨타임 entry를 제거(clear 시). */
  removeEntry(key: string): void;
}

/**
 * 키별 기간 쿨타임을 관리한다. 자체 상태는 없고 — 시작 시각·지속시간의 SSoT는 `CooldownStore`
 * (프로젝트 SaveData). 시계만 주입받아 현재 시각 대비 남은 시간을 산출한다.
 */
export class CooldownService {
  constructor(
    private readonly clock: Clock,
    private readonly store: CooldownStore,
  ) {}

  /** 쿨타임 시작 — 현재 시각 기준으로 `durationMs`만큼. 재호출 시 현재 시각으로 재시작. */
  start(key: string, durationMs: number): void {
    this.store.setEntry(key, { startedAtMs: this.clock(), durationMs });
  }

  /** 진행 중(남은 시간 > 0)인가. 미등록·만료 시 false. */
  isActive(key: string): boolean {
    return this.remainMs(key) > 0;
  }

  /** 남은 쿨타임(ms). 미등록·만료 시 0 (음수 없음). */
  remainMs(key: string): number {
    const entry = this.store.getEntry(key);
    if (!entry) return 0;
    return Math.max(0, entry.startedAtMs + entry.durationMs - this.clock());
  }

  /** 쿨타임 즉시 해제 — store에서 entry 제거. */
  clear(key: string): void {
    this.store.removeEntry(key);
  }

  /**
   * 남은 시간을 `ms`만큼 단축(광고 보상 등). 시작 시각을 앞당겨 처리 — 남은 시간보다 크면 즉시 만료.
   * idleblade `ReduceCooltimeSeconds` 대응. 미등록 키는 무동작. 음수 `ms`는 0으로 clamp(쿨타임 연장 방지).
   */
  reduce(key: string, ms: number): void {
    const entry = this.store.getEntry(key);
    if (!entry) return;
    this.store.setEntry(key, {
      startedAtMs: entry.startedAtMs - Math.max(0, ms),
      durationMs: entry.durationMs,
    });
  }
}
